/* eslint-disable complexity */
import {
	CheckboxCompat as Checkbox,
	DefaultButtonCompat as DefaultButton,
	DialogCompat as Dialog,
	DialogFooterCompat as DialogFooter,
	DialogType,
	IDialogContentProps,
	TextFieldCompat as TextField,
} from 'components/FluentUICompat';
import {useUserContext} from 'authentication/UserContext';
import {PeoplePicker} from 'components/hookForms/ControlledPeoplePicker';
import {GetWorkbenchItemsDocument} from 'features/Dashboard/hooks/workbenchItems.generated';
import {GetAllRegulationsDocument} from 'features/Regulations/hooks';
import {useUpdateRegulatoryDocumentMutation} from 'features/RegulatoryDocuments/hooks';
import {useCreateInterimRequirementMutation} from 'features/RegulatoryDocuments/hooks/useCreateInterimRequirment.generated';
import {useUpdateGetexRegulationMutation} from 'features/GetexRegulation/hooks/GetexRegulations.generated';
import {useInjectedData} from 'features/RegulatoryDocuments/RegDocDetailsPage/useInjectedData';
import {useDismantleDocumentMutation} from 'features/RegulatoryDocuments/hooks/useDismantleDocument.generated';
import {GetRegulatoryDocumentDetailsDocument} from 'features/RegulatoryDocuments/hooks/useGetRegulatoryDocumentDetails.generated';
import {convertToRef, mapToRef} from 'helpers';
import {useCommand, useNotificationBar} from 'hooks';
import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {
	Origin,
	RegulatoryDocument,
	WorkflowStatus,
	UserRole,
	UpdateRegulatoryDocumentInput,
} from 'types';
import {SplitDocumentLegend} from '../../SplitDocumentLegend';
import {
	LabelWithTooltip,
	ProviderThatEnablesGettingTooltipsFromContext,
	createTooltipTranslationProviderFromNamespace,
} from 'features/localizedTooltips';
import {ReasonField} from './ReasonField';
import {ActionButton} from 'components';
import {omit} from 'lodash';
import {
	isApprovalModified,
	mapWorkflowUserRefs,
} from 'features/RegulatoryDocuments/regulatoryUtils';
import {useMergeReapprovalRegulatoryDocumentChangesMutation} from 'features/RegulatoryDocuments/hooks/useMergeReapprovalRegulatoryDocumentChanges.generated';
import {
	useCheckIfHiddenLazyQuery,
	useMakeRegulationVisibleMutation,
} from 'features/Regulations/hooks/useCheckCanBeHidden.generated';
import {v4 as uuidv4} from 'uuid';
import {useBoolean} from '@fluentui/react-hooks';

enum WorkflowDialogType {
	None,
	Archive,
	Dismantle,
	QualityControlInternal,
	QualityControlExternal,
	ProgressExternal,
	Examination,
	Clearing,
	ClearingCancel,
	ClearingNOK,
	ClearingOK,
	ClearingWithReservations,
	ReapprovalOK,
	ReappovalNOK,
	SendToReapprove,
	ClearingPreCheckFailed,
	ClearingRegulationHidden,
}

const TooltipTranslationProvider =
	createTooltipTranslationProviderFromNamespace(
		'components/DocumentDetails/workflowDialog',
	);

const URL_4AUGEN =
	'https://porsche.sharepoint.com/sites/GETEXPorscheintern/SitePages/Vorschrift-freigeben.aspx';
/**
 * Implementation notes:
 *
 * We don't need to disable any commands here while the Edit Paragraphs Form is submitting
 * because the user is navigated to the homepage after submitting the mutation here.
 */
export const WorkflowDialog: React.FC<{
	regulatoryDocument: RegulatoryDocument;
	refetchAuditLogs?: () => void;
}> = ({regulatoryDocument, refetchAuditLogs}) => {
	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'WorkflowDialog',
	});

	const {username, isAdmin, encodedUserId} = useUserContext();
	const [mergeReapprovalRegulatoryDocumentChanges] =
		useMergeReapprovalRegulatoryDocumentChangesMutation();

	const [checkIfRegulationHidden] = useCheckIfHiddenLazyQuery();

	const [makeRegulationVisible] = useMakeRegulationVisibleMutation();

	const navigate = useNavigate();

	const [users, setUsers] = React.useState([]);

	const onUserInputChange = React.useCallback(
		(items: any) => setUsers(items || []),
		[users],
	);

	const [showDialog, setShowDialog] = React.useState<WorkflowDialogType>(
		WorkflowDialogType.None,
	);
	const [dialogSkipped, setDialogSkipped] = React.useState<WorkflowDialogType>(
		WorkflowDialogType.None,
	);

	const [internal, setInternal] = React.useState(true);

	// React.useEffect(() => setShowDialog(WorkflowDialogType.SendToReapprove), []);

	const showTitle = React.useCallback(() => {
		if (showDialog === WorkflowDialogType.Clearing) {
			return t('ConfirmClearing');
		}

		if (showDialog === WorkflowDialogType.ClearingCancel) {
			return t('ConfirmClearingCancel');
		}

		if (showDialog === WorkflowDialogType.ClearingOK) {
			return t('ClearingOKHeader');
		}

		if (showDialog === WorkflowDialogType.ClearingWithReservations) {
			return t('ClearingWithReservationsHeader');
		}

		if (showDialog === WorkflowDialogType.ClearingNOK) {
			return t('ClearingNOKHeader');
		}

		if (showDialog === WorkflowDialogType.ClearingPreCheckFailed) {
			return t('ClearingPreCheckFailed');
		}

		if (showDialog === WorkflowDialogType.ClearingRegulationHidden) {
			return t('ClearingRegulationHidden');
		}

		if (showDialog === WorkflowDialogType.ReapprovalOK) {
			return t('ReapprovinfOKHeader');
		}

		if (showDialog === WorkflowDialogType.ReappovalNOK) {
			return t('ReapprovinfNOKHeader');
		}

		if (showDialog === WorkflowDialogType.Archive) {
			return t('ConfirmArchive');
		}

		if (showDialog === WorkflowDialogType.Dismantle) {
			return internal
				? t('ConfirmDismantleInternal')
				: t('ConfirmDismantleExternal');
		}

		if (showDialog === WorkflowDialogType.QualityControlInternal) {
			return t('ConfirmFinishQualityControlInternal');
		}

		if (showDialog === WorkflowDialogType.QualityControlExternal) {
			return t('ConfirmFinishQualityControlExternal');
		}

		if (showDialog === WorkflowDialogType.ProgressExternal) {
			return t('ConfirmProgressExternal');
		}

		if (showDialog === WorkflowDialogType.Examination) {
			return t('ConfirmExamination');
		}

		if (showDialog === WorkflowDialogType.SendToReapprove) {
			return t('SendToReapprove');
		}
	}, [showDialog, t, internal]);

	const showSubText = React.useCallback(() => {
		if (showDialog === WorkflowDialogType.ClearingCancel) {
			return t('ClearingCancelText', {
				name: regulatoryDocument.name,
			});
		}

		if (showDialog === WorkflowDialogType.ClearingWithReservations) {
			return t('ClearingWithReservationsText', {
				name: regulatoryDocument.name,
			});
		}

		if (showDialog === WorkflowDialogType.Archive) {
			return t('ConfirmArchiveText', {
				name: regulatoryDocument.name,
			});
		}

		if (showDialog === WorkflowDialogType.Dismantle) {
			return internal
				? t('ConfirmDismantleInternalText', {name: regulatoryDocument.name})
				: t('ConfirmDismantleExternalText', {name: regulatoryDocument.name});
		}

		if (showDialog === WorkflowDialogType.QualityControlInternal) {
			return t('ConfirmFinishQualityControlText', {
				name: regulatoryDocument.name,
			});
		}

		if (showDialog === WorkflowDialogType.QualityControlExternal) {
			return t('ConfirmFinishQualityControlText', {
				name: regulatoryDocument.name,
			});
		}

		if (showDialog === WorkflowDialogType.ProgressExternal) {
			return t('ConfirmProgressExternalText', {
				name: regulatoryDocument.name,
			});
		}

		if (showDialog === WorkflowDialogType.Examination) {
			return t('ConfirmExaminationText', {
				name: regulatoryDocument.name,
			});
		}

		return '';
	}, [showDialog, regulatoryDocument, t]);

	const dialogContentProps: IDialogContentProps = React.useMemo(
		() => ({
			type: DialogType.normal,
			title: showTitle(),
			subText: showSubText(),
		}),
		[showTitle, showSubText],
	);

	const [updateRegulatoryDocument] = useUpdateRegulatoryDocumentMutation();
	const [updateGetexRegulation] = useUpdateGetexRegulationMutation();
	const {isGetexInjected, getUpdateGetexInput} = useInjectedData(
		regulatoryDocument.id,
		true,
	);
	const [createInterimRequirement] = useCreateInterimRequirementMutation();
	const [dismantleDocument] = useDismantleDocumentMutation();

	const [reason, setReason] = React.useState('');

	const updateRegulatoryDocumentCommon = (
		workflowStatus: WorkflowStatus,
		extraWorkflowProps: Record<string, any> = {},
		skipModificationDataUpdate?: boolean,
	) => {
		const input: UpdateRegulatoryDocumentInput = {
			id: regulatoryDocument.id,
			name: regulatoryDocument.name,
			status: regulatoryDocument.status,
			summary: regulatoryDocument.summary,
			dateEffective: regulatoryDocument.dateEffective,
			dateExpiration: regulatoryDocument.dateExpiration,
			predecessorRefs: (regulatoryDocument.predecessors ?? [])
				.filter(x => x.id !== undefined)
				.map(x => convertToRef(x)!),
			documentStatus: regulatoryDocument.documentStatus,
			topicManagementSummary: regulatoryDocument.topicManagementSummary,
			workflow: {
				status: workflowStatus,
				message: regulatoryDocument.workflow?.message,
				reapprovingMessage: regulatoryDocument.workflow?.reapprovingMessage,
				dueDate: regulatoryDocument.workflow?.dueDate,
				dueDateInProcess: regulatoryDocument.workflow?.dueDateInProcess,
				clearingTimestamp: regulatoryDocument.workflow?.clearingTimestamp,
				clearingWithReservationsTimestamp: regulatoryDocument.workflow?.clearingWithReservationsTimestamp,
				...mapWorkflowUserRefs(regulatoryDocument.workflow),
				...extraWorkflowProps,
			},
			regulationRef: convertToRef(regulatoryDocument.regulation),
			serviceProviderRefs: [],
			skipModificationDataUpdate,
			disassemblyStatus: regulatoryDocument.disassemblyStatus,
			pogisExists: regulatoryDocument.pogisExists,
			pogisRefs: (regulatoryDocument.pogisDocuments ?? [])
				.filter(x => x.id !== undefined)
				.map(x => convertToRef(x)!),
			documentReferences: regulatoryDocument.documentReferences
				?.filter(dr => dr !== undefined)
				.map(dr => {
					return {
						referenceDocumentType: dr.referenceDocumentType,
						reference: convertToRef(dr.reference)!,
						documentReferenceGroup: {
							regulationReference: convertToRef(
								dr.documentReferenceGroup.regulationReference,
							)!,
							regulatoryDocumentReferences: (
								dr.documentReferenceGroup?.regulatoryDocumentReferences ?? []
							)
								.filter(r => r?.id !== undefined)
								.map(r => convertToRef(r)!),
							selectAllRegulatoryDocuments:
								dr.documentReferenceGroup?.selectAllRegulatoryDocuments ??
								false,
						},
						selectAllRegulatoryDocuments:
							dr.documentReferenceGroup.selectAllRegulatoryDocuments,
					};
				}),
		};
		if (regulatoryDocument.finalVersionRef) {
			input.finalVersionRef = omit(
				regulatoryDocument.finalVersionRef,
				'__typename',
			);
		}

		if (isGetexInjected) {
			const updatedDoc = {
				...regulatoryDocument,
				workflow: {
					...regulatoryDocument.workflow,
					status: workflowStatus,
					...extraWorkflowProps,
				},
			};
			return updateGetexRegulation({
				variables: {
					input: getUpdateGetexInput(regulatoryDocument.id, updatedDoc),
				},
				refetchQueries: [
					GetRegulatoryDocumentDetailsDocument,
					GetAllRegulationsDocument,
					GetWorkbenchItemsDocument,
				],
			});
		}

		return updateRegulatoryDocument({
			variables: {
				input,
			},
			refetchQueries: [
				GetRegulatoryDocumentDetailsDocument,
				GetAllRegulationsDocument,
				GetWorkbenchItemsDocument,
			],
		});
	};

	const onArchiveClick = React.useCallback(async () => {
		await updateRegulatoryDocumentCommon(WorkflowStatus.Archived, {
			message: reason,
		})
			.then(() => {
				setShowDialog(WorkflowDialogType.None);
				setMessage(t('ArchiveSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [regulatoryDocument, reason, showDialog]);

	const onDismantleClick = React.useCallback(async () => {
		await updateRegulatoryDocumentCommon(
			internal
				? WorkflowStatus.QualityControlInternal
				: WorkflowStatus.QualityControlExternal,
		)
			.then(() => {
				dismantleDocument({
					variables: {
						input: regulatoryDocument.id,
						fastTrack: false,
					},
				}).catch(error => {
					console.error('Error:', error);
				});
				setShowDialog(WorkflowDialogType.None);
				setMessage(t('DismantleSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [regulatoryDocument, showDialog, internal]);

	const onFinishQualityControlInternalClick = React.useCallback(async () => {
		await updateRegulatoryDocumentCommon(WorkflowStatus.InProgressInternal)
			.then(() => {
				setShowDialog(WorkflowDialogType.None);
				setMessage(t('FinishQualityControlInternalSaveMessage'));
				navigate(0);
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [regulatoryDocument, showDialog]);

	const onFinishQualityControlExternalClick = React.useCallback(async () => {
		await updateRegulatoryDocumentCommon(WorkflowStatus.InProgressExternal)
			.then(() => {
				setShowDialog(WorkflowDialogType.None);
				setMessage(t('FinishQualityControlExternalSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [regulatoryDocument, showDialog]);

	const onProgressExternalClick = React.useCallback(async () => {
		await updateRegulatoryDocumentCommon(WorkflowStatus.InProgressExternal, {
			message: reason,
		})
			.then(() => {
				setShowDialog(WorkflowDialogType.None);
				setMessage(t('ProgressExternalSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [regulatoryDocument, showDialog, reason]);

	const onDeclineReapproval = useCallback(async () => {
		await updateRegulatoryDocumentCommon(
			WorkflowStatus.Modified,
			{
				message: reason,
				reapprovingVKO: null,
				clearingVKO: [],
			},
			true,
		)
			.then(() => {
				setShowDialog(WorkflowDialogType.None);
				setMessage(t('ProgressExternalSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [regulatoryDocument, showDialog, reason]);

	const onConfirmReapproval = React.useCallback(async () => {
		if (!regulatoryDocument?.finalVersionRef?.id) {
			console.error(`No final version regulation found!`);
			return;
		}

		await mergeReapprovalRegulatoryDocumentChanges({
			variables: {
				input: {
					finalRegulatoryDocumentId: regulatoryDocument.finalVersionRef.id,
					modifiedRegulatoryDocumentId: regulatoryDocument.id,
				},
			},
		})
			.then(() => {
				setShowDialog(WorkflowDialogType.None);
				navigate(`/regulations/${regulatoryDocument?.regulation?.id}`);
			})
			.catch(error => {
				console.error('Error:', error);
				setShowDialog(WorkflowDialogType.None);
			});
	}, [regulatoryDocument, showDialog, reason]);

	const onClearingClick = React.useCallback(async () => {
		await updateRegulatoryDocumentCommon(WorkflowStatus.Clearing, {
			clearingVKO: mapToRef(users),
		})
			.then(() => {
				setShowDialog(WorkflowDialogType.None);
				setMessage(t('ClearingSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [regulatoryDocument, users, showDialog]);

	const onClearingCancel = React.useCallback(async () => {
		await updateRegulatoryDocumentCommon(
			regulatoryDocument.workflow.status === WorkflowStatus.Clearing
				? WorkflowStatus.InProgressInternal
				: WorkflowStatus.Modified,
			{
				message: reason,
				reapprovingVKO: null,
				clearingVKO: [],
			},
			true,
		)
			.then(() => {
				setShowDialog(WorkflowDialogType.None);
				setMessage(t('ClearingCancelMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [regulatoryDocument, users, showDialog]);

	const onSendToReapprove = React.useCallback(async () => {
		await updateRegulatoryDocumentCommon(
			WorkflowStatus.Modified,
			{
				reapprovingMessage: reason,
				reapprovingVKO: convertToRef(users[0]),
			},
			true,
		)
			.then(() => {
				setShowDialog(WorkflowDialogType.None);
				refetchAuditLogs?.();
				navigate(`/regulations/${regulatoryDocument?.regulation?.id}`);
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [regulatoryDocument, showDialog, users, reason]);

	const onClearingWithReservationsClick = React.useCallback(async () => {
		await updateRegulatoryDocumentCommon(
			WorkflowStatus.ClearingWithReservations,
			{clearingVKO: mapToRef(regulatoryDocument.workflow.clearingVKO)},
		)
			.then(() => {
				setShowDialog(WorkflowDialogType.None);
				setMessage(t('ClearingSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [regulatoryDocument, showDialog]);

	const onClearingNOKClick = React.useCallback(async () => {
		await updateRegulatoryDocumentCommon(
			WorkflowStatus.InProgressInternal,
			{
				message: reason,
				reapprovingVKO: null,
				clearingVKO: [],
			},
			true,
		)
			.then(() => {
				setShowDialog(WorkflowDialogType.None);
				setMessage(t('ClearingNOKSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [regulatoryDocument, reason, showDialog]);

	const onClearingOKClick = React.useCallback(async () => {
		await updateRegulatoryDocumentCommon(
			WorkflowStatus.Finalized,
			{
				reapprovingVKO: null,
				clearingVKO: [],
			},
			true,
		)
			.then(() => {
				if (!isGetexInjected) {
					createInterimRequirement({
						variables: {regulatoryDocumentId: regulatoryDocument.id},
					});
				}
			})
			.then(() => {
				setShowDialog(WorkflowDialogType.None);
				setMessage(t('ClearingOKSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [regulatoryDocument, showDialog]);

	const onUnhideRegulation = React.useCallback(async () => {
		await makeRegulationVisible({
			variables: {regulationId: regulatoryDocument.regulation?.id ?? ''},
		});
		setShowDialog(WorkflowDialogType.None);
	}, [regulatoryDocument, showDialog]);

	const onExaminationClick = React.useCallback(async () => {
		await updateRegulatoryDocumentCommon(WorkflowStatus.Examination)
			.then(() => {
				setShowDialog(WorkflowDialogType.None);
				setMessage(t('ExaminationSaveMessage'));
				navigate('/');
			})
			.catch(error => {
				console.error('Error:', error);
			});
	}, [regulatoryDocument, showDialog]);

	const onDismiss = React.useCallback(
		() => setShowDialog(WorkflowDialogType.None),
		[],
	);

	const onInputChange = React.useCallback(
		(_: any, val?: string) => setReason(val ?? ''),
		[reason],
	);

	const isClearingVKO = React.useMemo(
		() =>
			encodedUserId === regulatoryDocument.workflow?.clearingVKO[0]?.id,
		[regulatoryDocument, encodedUserId],
	);

	useCommand(
		{
			key: 'archive',
			priority: 9,
			text: t('ArchiveRegulatoryDocument'),
			iconProps: {iconName: 'Archive'},
			onClick: () => setShowDialog(WorkflowDialogType.Archive),
			hidden:
				regulatoryDocument.workflow?.status !== WorkflowStatus.New ||
				regulatoryDocument.origin !== Origin.External,
			roles: [UserRole.Vko, UserRole.SystemAdministrator],
		},
		[showDialog, t, regulatoryDocument],
	);

	useCommand(
		{
			key: 'dismantle',
			priority: 10,
			text: t('DismantleRegulatoryDocument'),
			iconProps: {iconName: 'SplitObject'},
			hidden: regulatoryDocument.workflow?.status !== WorkflowStatus.New,
			disabled: !regulatoryDocument.document,
			roles: [UserRole.Vko, UserRole.SystemAdministrator],
			subMenuProps: {
				items: [
					{
						key: 'dismantleInternal',
						onClick() {
							setShowDialog(WorkflowDialogType.Dismantle);
							setInternal(true);
						},
						text: t('DismantleInternal'),
					},
					...(isAdmin
						? [
								{
									key: 'dismantleExternal',
									onClick() {
										setShowDialog(WorkflowDialogType.Dismantle);
										setInternal(false);
									},
									text: t('DismantleExternal'),
								},
						  ]
						: []),
				],
			},
		},
		[showDialog, t, regulatoryDocument, internal],
	);

	useCommand(
		{
			key: 'finishQualityControl',
			priority: 9,
			text: t('FinishQualityControlRegulatoryDocument'),
			iconProps: {iconName: 'ReleaseDefinition'},
			hidden:
				regulatoryDocument.workflow?.status !==
					WorkflowStatus.QualityControlInternal &&
				regulatoryDocument.workflow?.status !==
					WorkflowStatus.QualityControlExternal,
			roles: [
				UserRole.ServiceProvider,
				UserRole.SystemAdministrator,
				UserRole.Vko,
			],
			onClick: () =>
				regulatoryDocument.workflow?.status ===
				WorkflowStatus.QualityControlInternal
					? setShowDialog(WorkflowDialogType.QualityControlInternal)
					: setShowDialog(WorkflowDialogType.QualityControlExternal),
		},
		[showDialog, t, regulatoryDocument],
	);

	useCommand(
		{
			key: 'inProgressExternal',
			priority: 9,
			text: t('InProgressExternalRegulatoryDocument'),
			iconProps: {iconName: 'ReleaseDefinition'},
			onClick: () => setShowDialog(WorkflowDialogType.ProgressExternal),
			hidden:
				(regulatoryDocument.workflow?.status !==
					WorkflowStatus.InProgressInternal &&
					regulatoryDocument.workflow?.status !== WorkflowStatus.Examination) ||
				regulatoryDocument.workflow?.message === null,
			roles: [UserRole.VkoExternal, UserRole.SystemAdministrator],
		},
		[showDialog, t, regulatoryDocument],
	);

	useCommand(
		{
			key: 'sentToClearing',
			priority: 9,
			text: t('Clearing'),
			iconProps: {iconName: 'ReleaseGate'},
			onClick() {
				checkIfRegulationHidden({
					variables: {regulationId: regulatoryDocument.regulation?.id ?? ' '},
				}).then(x => {
					let boolCheckIfRegulationHidden: boolean | undefined = false;
					boolCheckIfRegulationHidden = x.data?.regulation?.hidden;
					if (boolCheckIfRegulationHidden) {
						setShowDialog(WorkflowDialogType.ClearingRegulationHidden);
					} else {
						checkIfParagraphsHaveNeededData(
							regulatoryDocument,
							WorkflowDialogType.Clearing,
							setShowDialog,
							setDialogSkipped,
						);
					}
				});
			},
			hidden:
				regulatoryDocument.workflow?.status !==
					WorkflowStatus.InProgressInternal &&
				regulatoryDocument.workflow?.status !== WorkflowStatus.Examination,
			roles: [UserRole.Vko, UserRole.SystemAdministrator],
		},
		[showDialog, t, regulatoryDocument, dialogSkipped],
	);

	useCommand(
		{
			key: 'clearingCancel',
			priority: 9,
			text: t('ClearingCancel'),
			iconProps: {iconName: 'PeopleBlock'},
			onClick: () => setShowDialog(WorkflowDialogType.ClearingCancel),
			hidden: !(
				regulatoryDocument.workflow?.status === WorkflowStatus.Clearing ||
				(regulatoryDocument.workflow?.status === WorkflowStatus.Modified &&
					isApprovalModified(regulatoryDocument.workflow?.reapprovingVKO?.id))
			),
			roles: [UserRole.Vko, UserRole.SystemAdministrator],
		},
		[showDialog, t, regulatoryDocument, dialogSkipped],
	);

	useCommand(
		{
			key: 'sendToReapprove',
			priority: 10,
			text: t('SendToReapprove'),
			iconProps: {iconName: 'ReleaseGate'},
			onClick() {
				checkIfParagraphsHaveNeededData(
					regulatoryDocument,
					WorkflowDialogType.SendToReapprove,
					setShowDialog,
					setDialogSkipped,
				);
			},
			hidden:
				regulatoryDocument.workflow?.status !== WorkflowStatus.Modified ||
				isApprovalModified(regulatoryDocument.workflow?.reapprovingVKO?.id),
			roles: [UserRole.Vko, UserRole.SystemAdministrator],
		},
		[showDialog, t, regulatoryDocument, dialogSkipped],
	);
	useCommand(
		{
			key: 'clearingOk',
			priority: 10,
			text: t('ClearingOK'),
			iconProps: {iconName: 'CheckMark'},
			onClick() {
				checkIfParagraphsHaveNeededData(
					regulatoryDocument,
					WorkflowDialogType.ClearingOK,
					setShowDialog,
					setDialogSkipped,
				);
			},
			hidden: !(
				(regulatoryDocument.workflow?.status === WorkflowStatus.Clearing ||
					regulatoryDocument.workflow?.status ===
						WorkflowStatus.ClearingWithReservations) &&
				(isClearingVKO || isAdmin)
			),
			roles: [UserRole.Vko, UserRole.SystemAdministrator],
		},
		[showDialog, t, regulatoryDocument, username, isClearingVKO, dialogSkipped],
	);

	useCommand(
		{
			key: 'ReapproveOk',
			priority: 10,
			text: t('ReapproveOk'),
			iconProps: {iconName: 'CheckMark'},
			onClick() {
				checkIfParagraphsHaveNeededData(
					regulatoryDocument,
					WorkflowDialogType.ReapprovalOK,
					setShowDialog,
					setDialogSkipped,
				);
			},
			hidden: !(
				regulatoryDocument.workflow?.status === WorkflowStatus.Modified &&
				regulatoryDocument.workflow?.reapprovingVKO?.id === encodedUserId
			),
			roles: [UserRole.Vko, UserRole.SystemAdministrator],
		},
		[showDialog, t, regulatoryDocument, username, isClearingVKO, dialogSkipped],
	);
	useCommand(
		{
			key: 'ReapproveNOk',
			priority: 10,
			text: t('ReapproveNOk'),
			iconProps: {iconName: 'CheckMark'},
			onClick: () => setShowDialog(WorkflowDialogType.ReappovalNOK),
			hidden: !(
				regulatoryDocument.workflow?.status === WorkflowStatus.Modified &&
				regulatoryDocument.workflow?.reapprovingVKO?.id === encodedUserId
			),
			roles: [UserRole.Vko, UserRole.SystemAdministrator],
		},
		[showDialog, t, regulatoryDocument, username, isClearingVKO],
	);

	useCommand(
		{
			key: 'clearingWithReservations',
			priority: 12,
			text: t('ClearingWithReservations'),
			iconProps: {iconName: 'ProgressRingDots'},
			onClick: () => setShowDialog(WorkflowDialogType.ClearingWithReservations),
			hidden: true,
			/* See ET164-1181: hide this button until further specification
					!(
				regulatoryDocument.workflow?.status === WorkflowStatus.Clearing &&
				username ===
					regulatoryDocument.workflow?.clearingVKO[0]?.userPrincipalName
			),
			*/
			roles: [UserRole.Vko, UserRole.SystemAdministrator],
		},
		[showDialog, t, regulatoryDocument, username],
	);

	useCommand(
		{
			key: 'clearingNotOk',
			priority: 12,
			text: t('ClearingNotOk'),
			iconProps: {iconName: 'Cancel'},
			onClick: () => setShowDialog(WorkflowDialogType.ClearingNOK),
			hidden: !(
				regulatoryDocument.workflow?.status === WorkflowStatus.Clearing &&
				(isClearingVKO || isAdmin)
			),
			roles: [UserRole.Vko, UserRole.SystemAdministrator],
		},
		[showDialog, t, regulatoryDocument, username, isClearingVKO],
	);

	useCommand(
		{
			key: 'examination',
			priority: 9,
			text: t('Examination'),
			iconProps: {iconName: 'ReleaseDefinition'},
			onClick: () => setShowDialog(WorkflowDialogType.Examination),
			hidden:
				regulatoryDocument.workflow?.status !==
				WorkflowStatus.InProgressExternal,
			roles: [UserRole.VkoExternal, UserRole.SystemAdministrator],
		},
		[showDialog, t, regulatoryDocument],
	);

	const {setMessage} = useNotificationBar();

	return (
		<TooltipTranslationProvider>
			<ProviderThatEnablesGettingTooltipsFromContext>
				{showDialog === WorkflowDialogType.Examination && (
					<Dialog
						hidden={!showDialog}
						dialogContentProps={dialogContentProps}
						modalProps={{
							isBlocking: true,
							styles: {main: {maxWidth: 450}},
						}}
						onDismiss={onDismiss}
					>
						<DialogFooter>
							<ActionButton onClick={onExaminationClick}>
								{t('ExaminationButton')}
							</ActionButton>
							<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
						</DialogFooter>
					</Dialog>
				)}
				{showDialog === WorkflowDialogType.Archive && (
					<Dialog
						hidden={!showDialog}
						dialogContentProps={dialogContentProps}
						modalProps={{
							isBlocking: true,
							styles: {main: {maxWidth: 450}},
						}}
						onDismiss={onDismiss}
					>
						<ReasonField
							onChange={onInputChange}
							required
							multiline
							autoAdjustHeight
							label={t('ReasonLabel')}
						/>
						<DialogFooter>
							<ActionButton onClick={onArchiveClick} disabled={!reason}>
								{t('ArchiveButton')}
							</ActionButton>
							<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
						</DialogFooter>
					</Dialog>
				)}
				{showDialog === WorkflowDialogType.Dismantle && (
					<Dialog
						hidden={!showDialog}
						dialogContentProps={dialogContentProps}
						modalProps={{
							isBlocking: true,
							styles: {main: {maxWidth: 450}},
						}}
						onDismiss={onDismiss}
					>
						<SplitDocumentLegend />

						<DialogFooter>
							<ActionButton onClick={onDismantleClick}>
								{t('DismantleButton')}
							</ActionButton>
							<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
						</DialogFooter>
					</Dialog>
				)}
				{showDialog === WorkflowDialogType.QualityControlInternal && (
					<Dialog
						hidden={!showDialog}
						dialogContentProps={dialogContentProps}
						modalProps={{
							isBlocking: true,
							styles: {
								main: {width: 700},
							},
						}}
						onDismiss={onDismiss}
					>
						<DialogFooter>
							<ActionButton onClick={onFinishQualityControlInternalClick}>
								{t('FinishQualityControlButton')}
							</ActionButton>
							<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
						</DialogFooter>
					</Dialog>
				)}
				{showDialog === WorkflowDialogType.QualityControlExternal && (
					<Dialog
						hidden={!showDialog}
						dialogContentProps={dialogContentProps}
						modalProps={{
							isBlocking: true,
							styles: {
								main: {width: 700},
							},
						}}
						onDismiss={onDismiss}
					>
						<DialogFooter>
							<ActionButton onClick={onFinishQualityControlExternalClick}>
								{t('FinishQualityControlButton')}
							</ActionButton>
							<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
						</DialogFooter>
					</Dialog>
				)}
				{showDialog === WorkflowDialogType.ProgressExternal && (
					<Dialog
						hidden={!showDialog}
						dialogContentProps={dialogContentProps}
						modalProps={{
							isBlocking: true,
							styles: {main: {maxWidth: 450}},
						}}
						onDismiss={onDismiss}
					>
						<ReasonField
							onChange={onInputChange}
							required
							multiline
							autoAdjustHeight
							label={t('ReasonLabel')}
						/>
						<DialogFooter>
							<ActionButton
								onClick={onProgressExternalClick}
								disabled={!reason}
							>
								{t('InProgressExternalButton')}
							</ActionButton>
							<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
						</DialogFooter>
					</Dialog>
				)}
				{showDialog === WorkflowDialogType.SendToReapprove && (
					<Dialog
						hidden={!showDialog}
						dialogContentProps={dialogContentProps}
						modalProps={{
							isBlocking: true,
							styles: {
								main: {
									maxWidth: '800px !important',
									width: '800px !important',
								},
							},
						}}
						onDismiss={onDismiss}
					>
						<ReasonField
							onChange={onInputChange}
							required
							multiline
							autoAdjustHeight
							maxLength={488}
							label={t('ReapprovalReasonLabel')}
						/>
						<LabelWithTooltip translationKey='users'>
							{t('ChoosePersonLabel')}
						</LabelWithTooltip>
						<PeoplePicker
							onChange={items => onUserInputChange(items ?? [])}
							itemLimit={1}
							excludeCurrentUser
							roles={[UserRole.Vko]}
						/>
						<DialogFooter>
							<ActionButton
								onClick={onSendToReapprove}
								disabled={users.length < 1 || !reason}
							>
								{t('ClearingButton')}
							</ActionButton>
							<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
						</DialogFooter>
					</Dialog>
				)}

				{showDialog === WorkflowDialogType.Clearing && (
					<Dialog
						hidden={!showDialog}
						dialogContentProps={dialogContentProps}
						modalProps={{
							isBlocking: true,
							styles: {main: {maxWidth: 450}},
						}}
						onDismiss={onDismiss}
					>
						<div
							dangerouslySetInnerHTML={{
								__html: t('ConfirmClearingText', {
									name: regulatoryDocument.name,
									url: URL_4AUGEN,
								}),
							}}
						/>
						<br />
						<LabelWithTooltip translationKey='users'>
							{t('ChoosePersonLabel')}
						</LabelWithTooltip>
						<PeoplePicker
							onChange={items => onUserInputChange(items ?? [])}
							itemLimit={1}
							excludeCurrentUser
							roles={[UserRole.Vko]}
						/>
						<DialogFooter>
							<ActionButton
								onClick={onClearingClick}
								disabled={users.length < 1}
							>
								{t('ClearingButton')}
							</ActionButton>
							<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
						</DialogFooter>
					</Dialog>
				)}
				{showDialog === WorkflowDialogType.ClearingCancel && (
					<Dialog
						hidden={!showDialog}
						dialogContentProps={dialogContentProps}
						modalProps={{
							isBlocking: true,
							styles: {main: {minWidth: 450}},
						}}
						onDismiss={onDismiss}
					>
						<DialogFooter>
							<ActionButton onClick={onClearingCancel}>
								{t('ClearingCancelButton')}
							</ActionButton>
							<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
						</DialogFooter>
					</Dialog>
				)}
				{showDialog === WorkflowDialogType.ClearingPreCheckFailed && (
					<WorkflowDialogPreCheckFailed
						hidden={!showDialog}
						regulatoryDocument={regulatoryDocument}
						dialogSkipped={dialogSkipped}
						onDismiss={onDismiss}
						onClick={setShowDialog}
						dialogContentProps={dialogContentProps}
					/>
				)}
				{showDialog === WorkflowDialogType.ClearingRegulationHidden && (
					<WorkflowDialogRegulationHidden
						hidden={!showDialog}
						regulatoryDocument={regulatoryDocument}
						onDismiss={onDismiss}
						onClick={onUnhideRegulation}
						dialogContentProps={dialogContentProps}
					/>
				)}
				{showDialog === WorkflowDialogType.ClearingOK && (
					<Dialog
						hidden={!showDialog}
						dialogContentProps={dialogContentProps}
						modalProps={{
							isBlocking: true,
							styles: {main: {maxWidth: 450}},
						}}
						onDismiss={onDismiss}
					>
						<div
							dangerouslySetInnerHTML={{
								__html: t('ClearingOKText', {
									name: regulatoryDocument.name,
									url: URL_4AUGEN,
								}),
							}}
						/>
						<br />
						<DialogFooter>
							<ActionButton onClick={onClearingOKClick}>
								{t('ClearingButton')}
							</ActionButton>
							<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
						</DialogFooter>
					</Dialog>
				)}
				{showDialog === WorkflowDialogType.ReapprovalOK && (
					<Dialog
						hidden={!showDialog}
						dialogContentProps={dialogContentProps}
						modalProps={{
							isBlocking: true,
							styles: {main: {maxWidth: 450}},
						}}
						onDismiss={onDismiss}
					>
						<div
							dangerouslySetInnerHTML={{
								__html: t('ClearingOKText', {
									name: regulatoryDocument.name,
									url: URL_4AUGEN,
								}),
							}}
						/>
						<br />
						<DialogFooter>
							<ActionButton onClick={onConfirmReapproval}>
								{t('ClearingButton')}
							</ActionButton>
							<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
						</DialogFooter>
					</Dialog>
				)}
				{showDialog === WorkflowDialogType.ReappovalNOK && (
					<Dialog
						hidden={!showDialog}
						dialogContentProps={dialogContentProps}
						modalProps={{
							isBlocking: true,
							styles: {main: {maxWidth: 450}},
						}}
						onDismiss={onDismiss}
					>
						<div
							dangerouslySetInnerHTML={{
								__html: t('ClearingNOKText', {
									name: regulatoryDocument.name,
									url: URL_4AUGEN,
								}),
							}}
						/>
						<br />
						<ReasonField
							onChange={onInputChange}
							required
							multiline
							autoAdjustHeight
							label={t('ReasonLabel')}
						/>
						<DialogFooter>
							<ActionButton onClick={onDeclineReapproval} disabled={!reason}>
								{t('Decline')}
							</ActionButton>
							<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
						</DialogFooter>
					</Dialog>
				)}
				{showDialog === WorkflowDialogType.ClearingWithReservations && (
					<Dialog
						hidden={!showDialog}
						dialogContentProps={dialogContentProps}
						modalProps={{
							isBlocking: true,
							styles: {main: {maxWidth: 450}},
						}}
						onDismiss={onDismiss}
					>
						<DialogFooter>
							<ActionButton onClick={onClearingWithReservationsClick}>
								{t('ClearingButton')}
							</ActionButton>
							<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
						</DialogFooter>
					</Dialog>
				)}
				{showDialog === WorkflowDialogType.ClearingNOK && (
					<Dialog
						hidden={!showDialog}
						dialogContentProps={dialogContentProps}
						modalProps={{
							isBlocking: true,
							styles: {main: {maxWidth: 450}},
						}}
						onDismiss={onDismiss}
					>
						<div
							dangerouslySetInnerHTML={{
								__html: t('ClearingNOKText', {
									name: regulatoryDocument.name,
									url: URL_4AUGEN,
								}),
							}}
						/>
						<br />
						<ReasonField
							onChange={onInputChange}
							required
							multiline
							autoAdjustHeight
							label={t('ReasonLabel')}
						/>
						<DialogFooter>
							<ActionButton onClick={onClearingNOKClick} disabled={!reason}>
								{t('ClearingButton')}
							</ActionButton>
							<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
						</DialogFooter>
					</Dialog>
				)}
			</ProviderThatEnablesGettingTooltipsFromContext>
		</TooltipTranslationProvider>
	);
};

const WorkflowDialogPreCheckFailed: React.FC<{
	hidden: boolean;
	regulatoryDocument: RegulatoryDocument;
	dialogSkipped: WorkflowDialogType;
	onDismiss: () => void;
	onClick: (value: any) => void;
	dialogContentProps: IDialogContentProps;
}> = ({
	hidden,
	regulatoryDocument,
	dialogSkipped,
	onDismiss,
	onClick,
	dialogContentProps,
}) => {
	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'WorkflowDialog',
	});

	const [skipCheck, {toggle: toggleSkip}] = useBoolean(false);

	return (
		<Dialog
			hidden={hidden}
			dialogContentProps={dialogContentProps}
			minWidth={450}
			modalProps={{
				isBlocking: true,
			}}
			onDismiss={onDismiss}
		>
			<div dangerouslySetInnerHTML={{__html: t('PreCheckFailedText')}}></div>
			<ul>
				{regulatoryDocument.paragraphs
					.filter(
						x =>
							x.categories.length === 0 ||
							x.driveVariants.length === 0 ||
							x.keywords.length === 0 ||
							x.vehicleCategories.length === 0,
					)
					.slice(0, 20)
					.map(x => (
						<li key={uuidv4()}>
							{x.enumeration.length > 0
								? x.enumeration
								: `Segment Nr. ${x.position}`}
						</li>
					))}
			</ul>
			<br />
			<Checkbox
				label={t('PreCheckFailedAcknowledge')}
				checked={skipCheck}
				onChange={toggleSkip}
			/>
			<DialogFooter>
				<ActionButton
					onClick={async () => onClick(dialogSkipped)}
					disabled={!skipCheck}
				>
					{t('PreCheckFailedSkip')}
				</ActionButton>
				<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
			</DialogFooter>
		</Dialog>
	);
};

const WorkflowDialogRegulationHidden: React.FC<{
	hidden: boolean;
	regulatoryDocument: RegulatoryDocument;
	onDismiss: () => void;
	onClick: (value?: any) => void;
	dialogContentProps: IDialogContentProps;
}> = ({hidden, onDismiss, onClick, dialogContentProps}) => {
	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'WorkflowDialog',
	});
	return (
		<Dialog
			hidden={hidden}
			dialogContentProps={dialogContentProps}
			modalProps={{
				isBlocking: true,
				styles: {main: {maxWidth: 450}},
			}}
			onDismiss={onDismiss}
		>
			<div>{t('RegulationHiddenText')}</div>
			<DialogFooter>
				<ActionButton onClick={async () => onClick()}>
					{t('RegulationMakeVisible')}
				</ActionButton>
				<DefaultButton onClick={onDismiss} text={t('CancelButton')} />
			</DialogFooter>
		</Dialog>
	);
};

const checkIfParagraphsHaveNeededData = (
	regulatoryDocument: RegulatoryDocument,
	dialogType: WorkflowDialogType,
	setShowDialog: (dialogType: WorkflowDialogType) => void,
	setShowDialogSkipped: (dialogType: WorkflowDialogType) => void,
) => {
	const boolCheckParagraphsHaveData =
		regulatoryDocument &&
		regulatoryDocument.paragraphs &&
		regulatoryDocument.paragraphs.filter(
			x =>
				x.categories.length > 0 &&
				x.driveVariants.length > 0 &&
				x.keywords.length > 0 &&
				x.vehicleCategories.length > 0,
		).length === regulatoryDocument.paragraphs.length;
	if (boolCheckParagraphsHaveData) {
		setShowDialog(dialogType);
	} else {
		setShowDialog(WorkflowDialogType.ClearingPreCheckFailed);
		setShowDialogSkipped(dialogType);
	}
};
